import type { ConnectionState } from '../../lib/sse/types.ts'
import { formatClock } from '../../lib/format.ts'
import { RelativeTime } from './RelativeTime.tsx'
import { StatusDot } from './StatusDot.tsx'
import type { Tone } from './Metric.tsx'

interface StateSpec {
  tone: Tone
  label: string
  /** Badge chrome. Distinct per state on more than colour alone. */
  chrome: string
}

const SPEC: Record<ConnectionState, StateSpec> = {
  connecting: {
    tone: 'accent',
    label: 'conectando',
    chrome: 'border-line text-fg-secondary',
  },
  live: {
    tone: 'ok',
    label: 'ao vivo',
    chrome: 'border-line-subtle text-fg-secondary',
  },
  stale: {
    tone: 'warn',
    label: 'sem dados',
    chrome: 'border-warn text-warn',
  },
  disconnected: {
    tone: 'crit',
    label: 'desconectado',
    chrome: 'border-crit text-crit',
  },
  forbidden: {
    tone: 'crit',
    label: 'sem permissão',
    chrome: 'border-crit text-crit',
  },
}

/**
 * The connection badge.
 *
 * Four states, four readings: colour, label, border weight and whether an age is
 * shown all change together. Colour alone would fail both a colour-blind
 * operator and a glance from three metres away.
 */
export function ConnectionBadge({
  state,
  lastEventAt,
}: {
  state: ConnectionState
  lastEventAt: number | null
}) {
  const spec = SPEC[state]

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-center gap-1.5 border px-2 py-0.5 text-micro uppercase tracking-label ${spec.chrome}`}
    >
      <StatusDot tone={spec.tone} />
      <span>{spec.label}</span>
      {state !== 'connecting' && state !== 'forbidden' && (
        <>
          <span aria-hidden="true" className="text-fg-muted">
            ·
          </span>
          <RelativeTime since={lastEventAt} />
        </>
      )}
    </div>
  )
}

/**
 * The honesty strip.
 *
 * Whenever the stream is not live, this states in words what the dimmed numbers
 * below already state visually: they are frozen, and this is when they were
 * true. A console that shows a stale number as if it were current is worse than
 * one that shows nothing, because the operator acts on it.
 */
export function StaleBanner({
  state,
  lastEventAt,
  serverTime,
}: {
  state: ConnectionState
  lastEventAt: number | null
  serverTime: string | null
}) {
  if (state === 'live') return null

  /**
   * Falta de papel não é falha de conexão, e não pode ser dita como se fosse.
   *
   * "Tentando reconectar" seria uma promessa falsa: nenhuma reconexão concede
   * `platform_staff`. O operador precisa saber que o caminho é outra pessoa, não
   * esperar.
   */
  if (state === 'forbidden') {
    return (
      <div
        role="alert"
        className="border-b border-crit px-3 py-1 text-sm text-crit"
        style={{ backgroundColor: 'color-mix(in oklab, var(--c-crit) 12%, var(--c-canvas))' }}
      >
        <span className="font-medium">Sua conta não tem acesso ao painel ao vivo.</span>
      </div>
    )
  }

  const hasData = lastEventAt !== null

  if (!hasData) {
    return (
      <div className="border-b border-line bg-inset px-3 py-1 text-sm text-fg-secondary">
        Conectando ao stream de métricas. Nenhum dado recebido ainda.
      </div>
    )
  }

  const critical = state === 'disconnected'

  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b px-3 py-1 text-sm ${
        critical ? 'border-crit text-crit' : 'border-warn text-warn'
      }`}
      style={{
        backgroundColor: critical
          ? 'color-mix(in oklab, var(--c-crit) 12%, var(--c-canvas))'
          : 'color-mix(in oklab, var(--c-warn) 12%, var(--c-canvas))',
      }}
    >
      <span className="font-medium">
        {critical ? 'Conexão perdida — tentando reconectar.' : 'Stream silencioso há mais de 10s.'}
      </span>
      <span className="text-fg-secondary">
        Último dado às{' '}
        <span className="tnum">{formatClock(serverTime)}</span> UTC (
        <RelativeTime since={lastEventAt} />
        ).
      </span>
    </div>
  )
}
